import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from './Icon.js';
import { useInoperative } from './inoperative.js';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading glyph. */
  icon?: IconName;
  /** Trailing glyph. */
  trailingIcon?: IconName;
  /**
   * Shows a spinner in place of the leading glyph, disables re-submission and keeps the width
   * stable (03-components §2). The label stays in the DOM so the width does not jump.
   */
  loading?: boolean;
  /** Full width; a form's primary action on mobile. */
  block?: boolean;
  children?: ReactNode;
}

/**
 * Why a reason travels with `disabled`: application baseline 1.1.0 05-client §5.5 — a control present but temporarily
 * inoperative "MUST explain why, reachable by pointer and by keyboard", and a native `disabled` cannot carry that, since
 * the control then takes neither hover nor focus; 03-components §2 says a disabled button is never the only explanation.
 * So a `disabled` control given a reason is NOT natively disabled: it stays focusable with `aria-disabled="true"`, its
 * action suppressed, the reason its `title` (hover), its accessible description (`aria-describedby` → a hidden element
 * after it, as MenuItem's detail) and a visible bubble on a resting keyboard focus or any press (`useInoperative`,
 * inoperative.tsx) — the owner's ruling J-D3 of 2026-10-06, "Keyboard route required" (Phase 1c findings D16, D17). A
 * `disabled` control with no reason, and a `loading` one — the loading state, which owes no reason (AB-Q7) — keep the
 * native attribute.
 */
export interface DisabledReasonProps {
  /** Why the control is disabled, as a short sentence: "Choose an account first." */
  disabledReason?: string | undefined;
}

export interface ButtonProps extends ButtonBaseProps, DisabledReasonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {}

export function buttonClasses({ variant = 'secondary', size = 'md', block = false, loading = false }: ButtonBaseProps, extra?: string): string {
  return ['btn', `btn--${variant}`, `btn--${size}`, block ? 'btn--block' : '', loading ? 'is-loading' : '', extra ?? ''].filter(Boolean).join(' ');
}

/**
 * A real `<button>`; `type` defaults to "button" so a stray click never submits a form. The reason's carriers sit AFTER
 * the button, never inside it: text inside a `<button>` joins its accessible name, and a sibling never does.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, trailingIcon, loading = false, block = false, children, className, disabled, disabledReason, title, type = 'button', 'aria-describedby': describedBy, onClick, onFocus, onBlur, onKeyDown, onPointerLeave, ...rest },
  ref,
) {
  const inoperative = useInoperative<HTMLButtonElement>(disabled && !loading && disabledReason ? disabledReason : undefined, { onClick, onFocus, onBlur, onKeyDown, onPointerLeave });
  const reason = inoperative.active ? disabledReason : undefined;
  return (
    <>
      <button
        ref={ref}
        type={type}
        className={buttonClasses({ variant, size, block, loading }, className)}
        // Why natively disabled only without a reason: an inoperative button with one stays focusable (above).
        disabled={loading || (disabled === true && !inoperative.active)}
        aria-busy={loading || undefined}
        title={reason ?? title}
        aria-describedby={inoperative.reasonId ?? describedBy}
        {...rest}
        {...inoperative.controlProps}
      >
        {loading ? <Spinner size={16} label="Working" /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 20} /> : null}
        {children !== undefined && children !== null ? <span className="btn__label">{children}</span> : null}
        {trailingIcon ? <Icon name={trailingIcon} size={size === 'sm' ? 16 : 20} /> : null}
      </button>
      {inoperative.reasonElements}
    </>
  );
});

export interface LinkButtonProps extends ButtonBaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> {
  /** Router path for in-app navigation… */
  to?: string;
  /** …or a plain href for server routes such as /auth/login (full navigation). */
  href?: string;
}

/** Navigation styled as a button: a real `<a>`, never a button with a click handler. */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function LinkButton(
  { variant = 'secondary', size = 'md', icon, trailingIcon, block = false, children, className, to, href, ...rest },
  ref,
) {
  const content = (
    <>
      {icon ? <Icon name={icon} size={size === 'sm' ? 16 : 20} /> : null}
      {children !== undefined && children !== null ? <span className="btn__label">{children}</span> : null}
      {trailingIcon ? <Icon name={trailingIcon} size={size === 'sm' ? 16 : 20} /> : null}
    </>
  );
  const classes = buttonClasses({ variant, size, block }, className);
  if (to !== undefined) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  return (
    <a ref={ref} href={href} className={classes} {...rest}>
      {content}
    </a>
  );
});

export interface IconButtonProps extends DisabledReasonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  icon: IconName;
  /** Required: an icon-only control must carry an accessible name (01-foundations §7.3). */
  label: string;
  variant?: Extract<ButtonVariant, 'secondary' | 'ghost' | 'danger-outline'>;
  size?: ButtonSize;
  /** A write in flight: the spinner, `aria-busy`, and the button disabled against a second submission (03-components §2). */
  loading?: boolean;
  /**
   * A wait that is not a write — a detail fetched before a dialog opens: the spinner and `aria-busy`, but the button
   * stays enabled and so keeps focus. Why: a focused button that becomes disabled drops focus to the body, and a
   * dialog opening next records the body as its opener and restores focus to nothing (§4.1, 01-foundations §7.2).
   * The caller refuses the repeat press itself.
   */
  busy?: boolean;
}

/**
 * Icon-only button: 20px glyph in a 36px box by default (02-patterns §1.3); the hit area grows to
 * 44px under a coarse pointer via .hit-area without enlarging the box.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', size = 'md', loading = false, busy = false, className, disabled, disabledReason, type = 'button', title, 'aria-describedby': describedBy, onClick, onFocus, onBlur, onKeyDown, onPointerLeave, ...rest },
  ref,
) {
  const waiting = loading || busy;
  const inoperative = useInoperative<HTMLButtonElement>(disabled && !loading && disabledReason ? disabledReason : undefined, { onClick, onFocus, onBlur, onKeyDown, onPointerLeave });
  const reason = inoperative.active ? disabledReason : undefined;
  return (
    <>
      <button
        ref={ref}
        type={type}
        className={['btn', 'btn--icon', `btn--${variant}`, `btn--${size}`, 'hit-area', waiting ? 'is-loading' : '', className ?? ''].filter(Boolean).join(' ')}
        aria-label={label}
        // Why the name stays in the title: an icon says less than a word, so the hover still names the control before
        // it says why the control is off.
        title={reason ? `${label} — ${reason}` : title ?? label}
        aria-describedby={inoperative.reasonId ?? describedBy}
        // Why natively disabled only without a reason: an inoperative button with one stays focusable (Button, above).
        disabled={loading || (disabled === true && !inoperative.active)}
        aria-busy={waiting || undefined}
        {...rest}
        {...inoperative.controlProps}
      >
        {waiting ? <Spinner size={16} label="Working" /> : <Icon name={icon} size={size === 'sm' ? 16 : 20} />}
      </button>
      {inoperative.reasonElements}
    </>
  );
});
